import {
    Box,
    Typography,
    IconButton,
    Divider,
    Button,
    Avatar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
    Snackbar,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useState, useRef, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { SIZE_TYPE_CONFIG } from '@/constants/sizeTypeConfig';
import { washOptions, normalizeWashValue } from '@/constants/washTypeConfig';
import { Product, WashInfo } from '@/services/matching.service';
import { materialValueToLabel } from '@/constants/materialTypeConfig';
import { extractOptionColor } from '@/utils/colorUtil';
import { COLOR_PALETTE } from '@/constants/colorTypeConfig';
import useCategoryStore from '@/store/categoryStore';
import { useVendorStore } from '@/store/vendorStore';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { isLogined } from '@/services/mallUser.service';
import mobile from 'is-mobile';

interface SelectedOption {
    quantity: number;
    price: number;
}

interface SelectedOptions {
    [key: string]: SelectedOption;
}

// 공통 스타일 상수
const DETAIL_TYPOGRAPHY_STYLES = {
    label: {
        color: '#222',
        fontWeight: 700,
        fontSize: 14,
    },
    value: {
        fontSize: 14,
    },
} as const;

interface FabricOptionRowProps {
    label: string;
    options: readonly string[];
    selectedValue?: string;
}

const FabricOptionRow = ({ label, options, selectedValue }: FabricOptionRowProps) => {
    const theme = useTheme();
    const isPc = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <Box>
            <Typography
                sx={{
                    width: '100px',
                    py: 1.5,
                    borderRight: '1px solid #eee',
                    color: '#222',
                    fontWeight: 700,
                    ...DETAIL_TYPOGRAPHY_STYLES.value,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                    gap: isPc ? 8 : 2,
                }}
            >
                {options.map((option) => (
                    <Typography
                        key={option}
                        sx={{
                            px: 2,
                            color: selectedValue === option ? '#222' : '#999',
                            fontWeight: selectedValue === option ? 700 : 400,
                            ...DETAIL_TYPOGRAPHY_STYLES.value,
                        }}
                    >
                        {option}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

// 옷감 정보 상수
const FABRIC_OPTIONS = {
    thickness: {
        options: ['얇음', '보통', '두꺼움'] as const,
        label: '두께감',
    },
    seeThrough: {
        options: ['없음', '보통', '있음'] as const,
        label: '비침',
    },
    elasticity: {
        options: ['없음', '보통', '좋음'] as const,
        label: '신축성',
    },
    lining: {
        options: ['없음', '있음', '기모안감'] as const,
        label: '안감',
    },
} as const;

interface ProductDetailContentProps {
    product: Product;
    vendorPath?: string;
    onBack: () => void;
}

const ProductDetailContent = ({ product, vendorPath, onBack }: ProductDetailContentProps) => {
    const { vendor } = useVendorStore();
    const rmProduct = product?.matchingProducts?.find((p) => p.id === product?.representProductId);
    const productImages = product?.groupImageUrl || [];

    const calculateDiscount = () => {
        if (rmProduct?.discountMethod === 'PERCENT') {
            return rmProduct.discountValue || 0;
        } else {
            const marketPrice = rmProduct?.marketPrice || 0;
            const supplyPrice = rmProduct?.supplyPrice || 0;
            if (marketPrice === 0 || supplyPrice >= marketPrice) return 0;
            return Math.floor(((marketPrice - supplyPrice) / marketPrice) * 100);
        }
    };

    const discount = calculateDiscount();

    // 모든 state와 ref를 최상단에 선언
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expandedAccordions, setExpandedAccordions] = useState({
        detail: true,
        care: true,
        size: true,
        desc: true,
        options: !isMobile,
    });
    const [totalPrice, setTotalPrice] = useState(0);

    useEffect(() => {
        setExpandedAccordions({
            detail: true,
            care: true,
            size: true,
            desc: true,
            options: !isMobile,
        });
        return () => {
            setExpandedAccordions({
                detail: true,
                care: false,
                size: false,
                desc: false,
                options: !isMobile,
            });
            setSelectedOptions({}); // 선택된 옵션 초기화
        };
    }, []);

    const imageRef = useRef(null);
    const [thumbnailStartIdx, setThumbnailStartIdx] = useState(0);
    const thumbnailRef = useRef<HTMLDivElement>(null);

    // 썸네일 네비게이션 상태
    const visibleThumbnails = isMobile ? 4 : 6;
    const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
    const [thumbnailTouchStart, setThumbnailTouchStart] = useState(0);
    const [thumbnailTouchEnd, setThumbnailTouchEnd] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(0);
    const [scrollStart, setScrollStart] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const [showScrollTop, setShowScrollTop] = useState(false);

    // 반응형 크기 설정
    const imgBoxWidth = isMobile ? '100vw' : 530;
    const imgBoxHeight = isMobile ? 707 : 707; // 모바일에서도 동일한 높이 사용
    const imageAspectRatio = '530/707'; // PC 이미지 비율 (530:707)

    // 이미지 배열 정보
    const totalImages = productImages.length;
    const hasMultipleImages = totalImages > 1;

    // 이미지 인덱스 초기화
    useEffect(() => {
        // 페이지 마운트 시 초기화
        setCurrentIndex(0);
        setThumbnailStartIdx(0);
        setThumbnailStartIndex(0);

        return () => {
            setSelectedOptions({});
            setTotalPrice(0);
            setDownloadProgress(0);
            setIsDownloading(false);
            setLoadedImages(new Set());
        };
    }, []);

    // 이벤트 핸들러 함수들
    const handleAccordion = (panel) => (_event, isExpanded) => {
        setExpandedAccordions((prev) => ({
            ...prev,
            [panel]: isExpanded,
        }));
    };

    // 다음 슬라이드로 이동
    const goToNext = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : prev));
    }, [hasMultipleImages, totalImages]);

    // 이전 슬라이드로 이동
    const goToPrev = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }, [hasMultipleImages]);

    // 터치 이벤트 핸들러
    const handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
        const clientX =
            e.type === 'mousedown' ? (e as React.MouseEvent).clientX : (e as React.TouchEvent).touches[0].clientX;
        setTouchStart(clientX);
        setTouchEnd(clientX);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
        const clientX =
            e.type === 'mousemove' ? (e as React.MouseEvent).clientX : (e as React.TouchEvent).touches[0].clientX;
        setTouchEnd(clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrev();
        }
    }, [touchStart, touchEnd, goToNext, goToPrev]);

    // 썸네일 네비게이션 함수들
    const goToPrevThumbnails = useCallback(() => {
        setThumbnailStartIndex((prev) => Math.max(0, prev - visibleThumbnails));
    }, [visibleThumbnails]);

    const goToNextThumbnails = useCallback(() => {
        setThumbnailStartIndex((prev) => Math.min(productImages.length - visibleThumbnails, prev + visibleThumbnails));
    }, [productImages.length, visibleThumbnails]);

    // 활성 썸네일이 보이도록 자동 스크롤
    const scrollToActiveThumbnail = useCallback(() => {
        const activeIndex = currentIndex;
        const startIndex = thumbnailStartIndex;
        const endIndex = startIndex + visibleThumbnails - 1;

        if (activeIndex < startIndex) {
            // 활성 썸네일이 왼쪽에 숨겨진 경우
            setThumbnailStartIndex(Math.max(0, activeIndex - Math.floor(visibleThumbnails / 2)));
        } else if (activeIndex > endIndex) {
            // 활성 썸네일이 오른쪽에 숨겨진 경우
            setThumbnailStartIndex(
                Math.min(productImages.length - visibleThumbnails, activeIndex - Math.floor(visibleThumbnails / 2)),
            );
        }
    }, [currentIndex, thumbnailStartIndex, visibleThumbnails, productImages.length]);

    // 현재 이미지 변경 시 썸네일 스크롤 조정
    useEffect(() => {
        scrollToActiveThumbnail();
    }, [currentIndex, scrollToActiveThumbnail]);

    // 썸네일 터치 이벤트 핸들러
    const handleThumbnailTouchStart = useCallback((e: React.TouchEvent) => {
        setThumbnailTouchStart(e.touches[0].clientX);
        setThumbnailTouchEnd(e.touches[0].clientX);
    }, []);

    const handleThumbnailTouchMove = useCallback((e: React.TouchEvent) => {
        setThumbnailTouchEnd(e.touches[0].clientX);
    }, []);

    const handleThumbnailTouchEnd = useCallback(() => {
        if (!thumbnailTouchStart || !thumbnailTouchEnd) return;

        const distance = thumbnailTouchStart - thumbnailTouchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && thumbnailStartIndex + visibleThumbnails < productImages.length) {
            goToNextThumbnails();
        } else if (isRightSwipe && thumbnailStartIndex > 0) {
            goToPrevThumbnails();
        }
    }, [
        thumbnailTouchStart,
        thumbnailTouchEnd,
        thumbnailStartIndex,
        visibleThumbnails,
        productImages.length,
        goToNextThumbnails,
        goToPrevThumbnails,
    ]);

    // 마우스 드래그 핸들러
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart(e.clientX);
        setScrollStart(thumbnailRef.current?.scrollLeft || 0);
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!isDragging || !thumbnailRef.current) return;

            const deltaX = e.clientX - dragStart;
            thumbnailRef.current.scrollLeft = scrollStart - deltaX;
            e.preventDefault();
        },
        [isDragging, dragStart, scrollStart],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    // 키보드 네비게이션
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!hasMultipleImages) return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    goToPrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasMultipleImages, goToPrev, goToNext]);

    const handleDownloadImages = async () => {
        try {
            setIsDownloading(true);
            setDownloadProgress(0);
            const totalImages = productImages.length;

            if (!mobile()) {
                for (const [index, imageUrl] of productImages.entries()) {
                    try {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                        link.href = url;
                        link.download = `image_${index + 1}_${timestamp}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                        setDownloadProgress(((index + 1) / totalImages) * 100);
                    } catch (error) {
                        console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
                    }
                }
                setSnackbarMessage('이미지 다운로드가 완료되었습니다.');
                setSnackbarOpen(true);
            } else {
                const zip = new JSZip();
                let successCount = 0;

                for (const [index, imageUrl] of productImages.entries()) {
                    try {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                        const blob = await response.blob();
                        zip.file(`image_${index + 1}.jpg`, blob);

                        successCount++;
                        setDownloadProgress((successCount / totalImages) * 100);
                    } catch (error) {
                        console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
                    }
                }

                if (successCount > 0) {
                    setDownloadProgress(100);

                    const content = await zip.generateAsync({
                        type: 'blob',
                        compression: 'DEFLATE',
                        compressionOptions: {
                            level: 9,
                        },
                    });

                    const url = window.URL.createObjectURL(content);
                    const link = document.createElement('a');
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    link.href = url;
                    link.download = `images_${timestamp}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    setSnackbarMessage('이미지 다운로드가 완료되었습니다.');
                    setSnackbarOpen(true);
                } else {
                    setSnackbarMessage('이미지 다운로드에 실패했습니다.');
                    setSnackbarOpen(true);
                }
            }
        } catch (error) {
            console.error('이미지 다운로드 중 오류 발생:', error);
            setSnackbarMessage('이미지 다운로드 중 오류가 발생했습니다.');
            setSnackbarOpen(true);
        } finally {
            setIsDownloading(false);
            setDownloadProgress(0);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // API 응답 예시 (실제로는 product prop으로 받아올 예정)

    interface WashOption {
        value: string;
        label: string;
        icon: string;
    }

    const renderWashInfo = () => {
        if (!product?.washInfo) return null;

        const allSelectedOptions: (WashOption & { category: string })[] = [];
        const addedValues = new Set<string>(); // 중복 제거를 위한 Set (카테고리+값 기준)

        // etc 배열 처리
        if (product.washInfo.etc && product.washInfo.etc?.length > 0) {
            product.washInfo.etc.forEach((etcValue) => {
                // 값 정규화
                const normalized = normalizeWashValue(etcValue);

                if (normalized) {
                    // 중복 체크 (카테고리 키 반영)
                    const key = `${normalized.category}:${normalized.value}`;
                    if (addedValues.has(key)) return;

                    // 정규화된 카테고리에서 찾기
                    const categoryOptions = washOptions[normalized.category] as WashOption[];
                    const matchingOption = categoryOptions.find((option) => option.value === normalized.value);
                    if (matchingOption) {
                        allSelectedOptions.push({
                            ...matchingOption,
                            category: 'etc',
                        });
                        addedValues.add(key);
                    }
                }
            });
        }

        // 다른 필드들 처리 (문자열 값들)
        const stringFields = ['dry', 'iron', 'bleach', 'method', 'special', 'detergent'] as const;
        stringFields.forEach((field) => {
            const value = product.washInfo[field];
            if (value) {
                // 값 정규화
                const normalized = normalizeWashValue(value);
                const searchValue = normalized ? normalized.value : value;

                // 중복 체크 (카테고리 키 반영)
                const key = `${field}:${searchValue}`;
                if (addedValues.has(key)) return;

                const categoryOptions = washOptions[field] as WashOption[];
                const matchingOption = categoryOptions.find((option) => option.value === searchValue);
                if (matchingOption) {
                    allSelectedOptions.push({
                        ...matchingOption,
                        category: field,
                    });
                    addedValues.add(key);
                }
            }
        });

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allSelectedOptions.map((option) => (
                    <Tooltip key={`${option.category}-${option.value}`} title={option.label} placement="top">
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                px: 1.5,
                                py: 1,
                            }}
                        >
                            <img
                                src={option.icon}
                                alt={option.label}
                                style={{
                                    width: 20,
                                    height: 20,
                                    objectFit: 'contain',
                                }}
                            />
                            <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        );
    };

    // 이미지 로딩 처리만 남김
    useEffect(() => {
        if (!product?.description) return;

        const handleImageLoad = (event: Event) => {
            const img = event.target as HTMLImageElement;
            setLoadedImages((prev) => new Set([...prev, img.src]));
        };

        const images = document.querySelectorAll('.product-description img');
        images.forEach((img) => {
            const imgElement = img as HTMLImageElement;

            if (imgElement.complete && imgElement.naturalWidth > 0) {
                setLoadedImages((prev) => new Set([...prev, imgElement.src]));
            } else {
                imgElement.addEventListener('load', handleImageLoad);
            }
        });

        return () => {
            images.forEach((img) => {
                img.removeEventListener('load', handleImageLoad);
            });
        };
    }, [product?.description]);

    const handleLoginClick = () => {
        setIsOpen(true);
    };

    const handleQuantityChange = (id, change) => {
        const option = product?.matchingProducts?.find((p) => p.id === id);
        setSelectedOptions((prev) => {
            const currentQty = prev[id]?.quantity || 0;
            const newQty = Math.max(0, currentQty + change);

            if (newQty === 0) {
                const newOptions = { ...prev };
                delete newOptions[id];
                return newOptions;
            }

            return {
                ...prev,
                [id]: {
                    quantity: newQty,
                    price: option?.supplyPrice,
                },
            };
        });
    };

    useEffect(() => {
        const _totalPrice = Object.values(selectedOptions).reduce((total: number, option: SelectedOption) => {
            return total + option.quantity * option.price;
        }, 0);
        setTotalPrice(_totalPrice);
    }, [selectedOptions]);

    // 옵션 정보 추출 함수
    const extractOptionValues = (optionType: 'color' | 'size') => {
        if (!product?.optionShowOrder || !product?.productInfo) {
            return '';
        }

        const uniqueValues = new Set<string>();

        // 키워드 정의 (모두 소문자로)
        const keywords = {
            color: ['color', '컬러', '색상'],
            size: ['size', '사이즈'],
        };

        product.productInfo.forEach((info) => {
            info.options
                .filter((opt) => {
                    // optionKey 또는 option_key 처리 (소문자로 변환)
                    const key = (opt.optionKey || opt.option_key || '').toLowerCase();
                    const targetKeywords = keywords[optionType];

                    // 소문자로 변환된 키와 비교
                    return targetKeywords.some((keyword) => key === keyword);
                })
                .forEach((opt) => {
                    // optionValue 또는 option_value 처리
                    const value = opt?.optionValue || opt?.option_value;
                    if (value) {
                        if (optionType === 'color' && value?.startsWith('#')) {
                            // colorNameInfo에서 먼저 찾기
                            const customName = product?.colorNameInfo?.[value];
                            if (customName) {
                                uniqueValues.add(customName);
                            } else {
                                // COLOR_PALETTE에서 찾기
                                const colorInfo = COLOR_PALETTE?.find(
                                    (c) => c.value?.toLowerCase() === value?.toLowerCase(),
                                );
                                uniqueValues.add(colorInfo ? colorInfo?.name : value);
                            }
                        } else {
                            uniqueValues.add(value);
                        }
                    }
                });
        });

        // 정렬된 배열로 변환
        const sortedValues = Array.from(uniqueValues).sort((a, b) => {
            // 사이즈의 경우 숫자 기준으로 정렬
            if (optionType === 'size') {
                const getNumber = (str: string) => parseInt(str.match(/\d+/)?.[0] || '0');
                return getNumber(a) - getNumber(b);
            }
            return a.localeCompare(b);
        });

        return sortedValues.join(', ');
    };

    // 옵션 정보 추출
    const colorInfo = extractOptionValues('color');
    const sizeInfo = extractOptionValues('size');

    const getCategoryById = useCategoryStore((state) => state.getCategoryById);
    const { setIsOpen } = useLoginFormDialogStore((state) => state.action);

    // product가 있을 때만 카테고리 정보 조회
    const category = product ? getCategoryById(product.categoryId) : undefined;

    // 세탁 및 상품 주의사항 표시 여부를 확인하는 함수
    const shouldShowCareInfo = () => {
        const hasFabricInfo = product?.fabricInfo && Object.values(product.fabricInfo).some((value) => value);
        const hasWashInfo =
            product?.washInfo && Object.values(product.washInfo).some((values) => values && values.length > 0);
        return hasFabricInfo || hasWashInfo;
    };

    // 제품설명 썸네일(대표/그룹 이미지) 렌더링 유틸
    const getDetailThumbnailImages = (): string[] => {
        const images: string[] = [];
        if (product?.representImageUrl) images.push(product.representImageUrl);
        if (Array.isArray(product?.groupImageUrl)) images.push(...product.groupImageUrl);
        return Array.from(new Set(images.filter(Boolean)));
    };

    const shouldRenderDetailThumbnails = product?.useThumbnailInDetail === 't';
    const isThumbPositionTop = product?.thumbnailInDetailPosition === 'top';

    const renderDetailThumbnails = () => {
        const images = getDetailThumbnailImages();
        if (!images.length) return null;
        return (
            <Box className="product-description">
                {images.map((img, idx) => (
                    <img key={`detail-thumb-${idx}`} src={img} alt={`thumbnail-${idx}`} loading="lazy" />
                ))}
            </Box>
        );
    };

    // 조건부 렌더링
    if (!product) return null;

    const currentSizeConfig = SIZE_TYPE_CONFIG.find((config) => config.value === product.sizeType);

    const handleAddToCart = () => {
        const hasSelectedOptions = Object.keys(selectedOptions).length > 0;
        if (!hasSelectedOptions) {
            setSnackbarMessage('옵션을 선택해주세요.');
            setSnackbarOpen(true);
            return;
        }
        // TODO: 장바구니 추가 로직 구현
        setSnackbarMessage('장바구니에 상품이 추가되었습니다.');
        setSnackbarOpen(true);
    };

    return (
        <>
            <Box
                onScroll={(e) => {
                    const top = e.currentTarget.scrollTop || 0;
                    if (top > 300) {
                        setShowScrollTop(true);
                    } else {
                        setShowScrollTop(false);
                    }
                }}
                id="mainContentRef"
                sx={{
                    padding: 0,
                    height: '100%',
                    overflow: 'auto',
                }}
            >
                {/* 메인 컨텐츠 영역 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        px: { xs: 0, md: 4 },
                        py: { xs: 0, md: 2 },
                    }}
                >
                    {/* 상품 정보 컨테이너 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 2, md: 5.5 },
                            mb: { xs: 1, md: 4 },
                        }}
                    >
                        {/* 모바일 뒤로가기 버튼 */}
                        {isMobile && (
                            <IconButton
                                onClick={onBack}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: 16,
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    zIndex: 10,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,1)',
                                    },
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )}

                        {/* 좌측: 이미지/슬라이더 */}
                        <Box
                            sx={{
                                width: { xs: '100%', md: imgBoxWidth },
                                minWidth: { xs: '100%', md: imgBoxWidth },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flexShrink: 0,
                                position: 'relative',
                            }}
                        >
                            {/* 이미지 슬라이더 */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: { xs: 'auto', md: imgBoxHeight },
                                    aspectRatio: imageAspectRatio,
                                    mb: 2,
                                    bgcolor: '#fff',
                                    borderRadius: { xs: 0, md: 2 },
                                    overflow: 'hidden',
                                    cursor: 'default',
                                }}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onMouseDown={handleTouchStart}
                                onMouseMove={handleTouchMove}
                                onMouseUp={handleTouchEnd}
                                onMouseLeave={handleTouchEnd}
                            >
                                {/* 이미지 컨테이너 */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: hasMultipleImages ? `${totalImages * 100}%` : '100%',
                                        height: '100%',
                                        transform: hasMultipleImages
                                            ? `translateX(-${(currentIndex * 100) / totalImages}%)`
                                            : 'translateX(0%)',
                                        transition: 'transform 0.2s ease-in-out',
                                        willChange: 'transform',
                                    }}
                                >
                                    {productImages.map((image, index) => (
                                        <Box
                                            key={`${image}-${index}`}
                                            sx={{
                                                width: hasMultipleImages ? `${100 / totalImages}%` : '100%',
                                                height: '100%',
                                                position: 'relative',
                                            }}
                                        >
                                            <img
                                                src={image}
                                                alt={product.groupTitle}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                    userSelect: 'none',
                                                    WebkitUserSelect: 'none',
                                                    pointerEvents: 'none',
                                                }}
                                                draggable={false}
                                                loading="lazy"
                                            />
                                        </Box>
                                    ))}
                                </Box>

                                {/* 좌우 화살표 - PC에서만 표시 */}
                                {!isMobile && hasMultipleImages && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            px: 2,
                                            '&:hover': {
                                                opacity: 1,
                                            },
                                        }}
                                    >
                                        <IconButton
                                            onClick={goToPrev}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.8)',
                                                borderRadius: 1.5,
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                },
                                            }}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={goToNext}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.8)',
                                                borderRadius: 1.5,
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                },
                                            }}
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                )}
                                {/* 인디케이터 */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 10,
                                        bottom: 10,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: '#fff',
                                        borderRadius: 2,
                                        px: 1.2,
                                        py: 0.5,
                                        fontSize: 13,
                                    }}
                                >
                                    {hasMultipleImages ? `${currentIndex + 1} / ${totalImages}` : `1 / ${totalImages}`}
                                </Box>
                            </Box>

                            {/* 다운로드 버튼 */}
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mb: 2,
                                    px: { xs: 2, md: 0 },
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                                    onClick={handleDownloadImages}
                                    disabled={isDownloading}
                                    sx={{
                                        borderColor: '#222',
                                        color: '#222',
                                        height: 48,
                                        borderRadius: 1.5,
                                        position: 'relative',
                                        '&:hover': {
                                            borderColor: '#000',
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                        },
                                    }}
                                >
                                    {isDownloading ? (
                                        <>
                                            다운로드 중... {Math.round(downloadProgress)}%
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    width: `${downloadProgress}%`,
                                                    height: '2px',
                                                    bgcolor: '#222',
                                                    transition: 'width 0.3s ease',
                                                }}
                                            />
                                        </>
                                    ) : (
                                        '이미지 다운로드'
                                    )}
                                </Button>
                            </Box>

                            {/* 썸네일 리스트 */}
                            <Box
                                sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    mb: { xs: 2, md: 0 },
                                    px: { xs: 2, md: 0 },
                                }}
                            >
                                <Box
                                    ref={thumbnailRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseLeave}
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        width: '100%',
                                        position: 'relative',
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        overflowX: 'auto',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        '&::-webkit-scrollbar': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    {productImages.map((img, idx) => {
                                        const isActive = idx === currentIndex;

                                        return (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    position: 'relative',
                                                    border: isActive ? '3px solid #222' : '1px solid #eee',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    width: `calc((100% - 5px * 5) / 6)`,
                                                    minWidth: `calc((100% - 5px * 5) / 6)`,
                                                    aspectRatio: '1/1',
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                }}
                                                onClick={() => {
                                                    if (hasMultipleImages) {
                                                        setCurrentIndex(idx);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={product.groupTitle}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        userSelect: 'none',
                                                    }}
                                                    draggable={false}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>

                        {/* 우측: 상품 정보 영역 */}
                        <Box
                            sx={{
                                flex: 1,
                                // minWidth: { xs: 0, md: 500 },
                                display: 'flex',
                                flexDirection: 'column',
                                px: { xs: 2, md: 0 },
                                flexShrink: 1,
                            }}
                        >
                            {/* 모바일에서만 보이는 카테고리 */}
                            {isMobile && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: '#888',
                                            fontWeight: 400,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {category?.wholeCategoryName?.split('>')[0] || ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: '#888' }}>&gt;</Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: '#222',
                                            fontWeight: 400,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {category?.wholeCategoryName?.split('>')[1] || ''}
                                    </Typography>
                                </Box>
                            )}

                            {/* PC에서만 보이는 판매자 정보 */}
                            {!isMobile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Avatar
                                        src={product.vendor?.imageUrl || ''}
                                        sx={{ width: 44, height: 44, mr: 2 }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                                            {product.vendor?.nickname || ''}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#888' }}>
                                            {vendor?.vendorBusinessInfo?.storeLocation &&
                                            vendor?.vendorBusinessInfo?.storeName ? (
                                                <>
                                                    {' '}
                                                    {vendor?.vendorBusinessInfo?.storeLocation
                                                        ?.split(',')
                                                        .join(' ')}{' '}
                                                    {vendor?.vendorBusinessInfo?.storeName}
                                                </>
                                            ) : (
                                                <>매장 위치 판매자문의</>
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* 상품명/가격/아이콘 */}
                            <Typography variant="h1" sx={{ fontSize: 24, fontWeight: 700, lineHeight: '34px', mb: 2 }}>
                                {product.groupTitle}
                            </Typography>
                            <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
                                {isLogined() ? (
                                    <>
                                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#f00' }}>
                                                    {discount}%
                                                </Typography>
                                                <Typography
                                                    sx={{ fontSize: 16, color: '#999', textDecoration: 'line-through' }}
                                                >
                                                    ₩
                                                    {(rmProduct?.marketPrice
                                                        ? rmProduct.marketPrice
                                                        : 0
                                                    )?.toLocaleString()}
                                                </Typography>
                                            </Box> */}
                                        <Typography sx={{ fontSize: 28, fontWeight: 700 }}>
                                            ₩{rmProduct?.supplyPrice?.toLocaleString()}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography sx={{ fontSize: 16, color: '#666', fontWeight: 500 }}>
                                        판매가 회원공개
                                    </Typography>
                                )}
                            </Box>

                            {/* 모바일에서만 보이는 판매자 정보 */}
                            {isMobile && (
                                <>
                                    <Divider sx={{ borderColor: '#eee', my: { xs: 1, md: 3 } }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                                        <Avatar
                                            src={product.vendor?.imageUrl || ''}
                                            sx={{ width: 32, height: 32, mr: 2 }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                                {product.vendor?.nickname || ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: '#888' }}>
                                                {vendor?.vendorBusinessInfo?.storeLocation &&
                                                vendor?.vendorBusinessInfo?.storeName ? (
                                                    <>
                                                        {' '}
                                                        {vendor?.vendorBusinessInfo?.storeLocation
                                                            ?.split(',')
                                                            .join(' ')}{' '}
                                                        {vendor?.vendorBusinessInfo?.storeName}
                                                    </>
                                                ) : (
                                                    <>매장 위치 판매자문의</>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {/* 구분선 추가 */}
                            <Divider sx={{ borderColor: '#000', borderBottomWidth: 2 }} />

                            {/* 상세정보 아코디언 */}
                            {!isMobile && (
                                <Accordion
                                    expanded={expandedAccordions.detail}
                                    onChange={handleAccordion('detail')}
                                    sx={{ boxShadow: 'none', mb: 1 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                    >
                                        상세정보
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '80px 1fr', md: '120px 1fr' },
                                                rowGap: 1,
                                                columnGap: 2,
                                            }}
                                        >
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>제조국</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                {product?.countryOfOrigin || '-'}
                                            </Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>색상</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                {colorInfo || '-'}
                                            </Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>사이즈</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                {sizeInfo || '-'}
                                            </Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>혼용률</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                {product?.materialInfo && product.materialInfo.length > 0
                                                    ? product.materialInfo
                                                          .map(
                                                              (m) =>
                                                                  `${materialValueToLabel(m.material)} ${
                                                                      m.percentage
                                                                  }%`,
                                                          )
                                                          .join(', ')
                                                    : '판매자문의'}
                                            </Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>낱장여부</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>판매자문의</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>상품등록정보</Typography>
                                            <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                {product?.createdAt?.split('T')[0]} 등록
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* 구분선 추가 */}
                            <Divider sx={{ borderColor: '#eee' }} />

                            {/* 세탁 및 상품 주의사항 아코디언 */}
                            {!isMobile && shouldShowCareInfo() && (
                                <Accordion
                                    expanded={expandedAccordions.care}
                                    onChange={handleAccordion('care')}
                                    sx={{ boxShadow: 'none', mb: 1 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                    >
                                        세탁 및 상품 주의사항
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ mb: 1, fontSize: { xs: 13, md: 15 } }}>
                                            {product?.fabricInfo &&
                                                Object.values(product.fabricInfo).some((value) => value) && (
                                                    <>
                                                        <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                            옷감 정보
                                                        </Typography>

                                                        {/* 두꺼운 구분선 추가 */}
                                                        <Divider
                                                            sx={{
                                                                my: 3,
                                                                borderBottomWidth: 1,
                                                                borderColor: '#222',
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                width: '100%',
                                                                mb: 3,
                                                                '& > div': {
                                                                    display: 'flex',
                                                                    borderBottom: '1px solid #eee',
                                                                    '&:last-child': {
                                                                        borderBottom: 'none',
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {/* 옷감 정보 섹션 */}
                                                            {Object.entries(FABRIC_OPTIONS).map(
                                                                ([key, { label, options }]) => (
                                                                    <FabricOptionRow
                                                                        key={key}
                                                                        label={label}
                                                                        options={options}
                                                                        selectedValue={
                                                                            product?.fabricInfo?.[
                                                                                key as keyof typeof FABRIC_OPTIONS
                                                                            ]
                                                                        }
                                                                    />
                                                                ),
                                                            )}
                                                        </Box>
                                                    </>
                                                )}
                                            {product?.washInfo &&
                                                Object.values(product.washInfo).some(
                                                    (values) => values && values.length > 0,
                                                ) && (
                                                    <>
                                                        <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                            세탁 정보
                                                        </Typography>
                                                        {renderWashInfo()}
                                                    </>
                                                )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* 구분선 추가 */}
                            <Divider sx={{ borderColor: '#eee' }} />

                            {/* 상품 옵션 아코디언 */}
                            {!isMobile && (
                                <Accordion
                                    expanded={expandedAccordions.options}
                                    onChange={handleAccordion('options')}
                                    sx={{ boxShadow: 'none', mb: 1 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                    >
                                        상품 옵션
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* 옵션 목록 */}
                                            {product?.matchingProducts
                                                ?.filter((p) => p?.isActivated !== 'f' && p?.isPublic !== 'f')
                                                .map((option) => {
                                                    const newOption = extractOptionColor(option, product.colorNameInfo);
                                                    return (
                                                        <Box
                                                            key={'optionList_' + option.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                pb: 2,
                                                                borderBottom: '1px solid #eee',
                                                                '&:last-child': {
                                                                    borderBottom: 'none',
                                                                    pb: 0,
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                {(newOption?.optionImageUrl || newOption?.color) && (
                                                                    <Box
                                                                        sx={{
                                                                            width: 48,
                                                                            height: 48,
                                                                            borderRadius: 1,
                                                                            overflow: 'hidden',
                                                                            border: '1px solid #eee',
                                                                        }}
                                                                    >
                                                                        {newOption?.optionImageUrl ? (
                                                                            <img
                                                                                src={newOption?.optionImageUrl}
                                                                                alt={newOption?.optionName}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover',
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Box
                                                                                sx={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    bgcolor: newOption?.color,
                                                                                    border: '1px solid #eee',
                                                                                    borderRadius: 1,
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                                <Box>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            ...(!newOption?.optionImageUrl &&
                                                                            !newOption?.color
                                                                                ? {
                                                                                      display: 'inline-block',
                                                                                      mr: 1,
                                                                                  }
                                                                                : {
                                                                                      mb: 0.2,
                                                                                  }),
                                                                        }}
                                                                    >
                                                                        {newOption.optionName}
                                                                    </Typography>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            fontWeight: 700,
                                                                            ...(!newOption?.optionImageUrl &&
                                                                            !newOption?.color
                                                                                ? {
                                                                                      display: 'inline-block',
                                                                                  }
                                                                                : {}),
                                                                        }}
                                                                    >
                                                                        {isLogined() ? (
                                                                            <>
                                                                                ₩
                                                                                {newOption?.supplyPrice?.toLocaleString()}
                                                                            </>
                                                                        ) : (
                                                                            <></>
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                {isLogined() ? (
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: 1,
                                                                            overflow: 'hidden',
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            onClick={() =>
                                                                                handleQuantityChange(newOption?.id, -1)
                                                                            }
                                                                            sx={{
                                                                                minWidth: 32,
                                                                                height: 32,
                                                                                borderRadius: 0,
                                                                                borderRight: '1px solid #ddd',
                                                                                color: '#666',
                                                                            }}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Typography
                                                                            sx={{
                                                                                width: 40,
                                                                                textAlign: 'center',
                                                                                fontSize: 13,
                                                                            }}
                                                                        >
                                                                            {selectedOptions[newOption?.id]?.quantity ||
                                                                                0}
                                                                        </Typography>
                                                                        <Button
                                                                            onClick={() =>
                                                                                handleQuantityChange(newOption?.id, 1)
                                                                            }
                                                                            sx={{
                                                                                minWidth: 32,
                                                                                height: 32,
                                                                                borderRadius: 0,
                                                                                borderLeft: '1px solid #ddd',
                                                                                color: '#666',
                                                                            }}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </Box>
                                                                ) : (
                                                                    <Typography sx={{ fontSize: 13, color: '#999' }}>
                                                                        판매가 회원공개
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}

                                            {/* 총 금액 */}
                                            {isLogined() ? (
                                                <Box
                                                    sx={{
                                                        mt: 1,
                                                        p: 2,
                                                        bgcolor: '#f8f8f8',
                                                        borderRadius: 1,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                                        총 금액(VAT 별도)
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                                                        ₩{totalPrice.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        mt: 1,
                                                        p: 2,
                                                        bgcolor: '#f8f8f8',
                                                        borderRadius: 1,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 14, color: '#666' }}>
                                                        로그인 후 판매가를 확인해주세요
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </Box>
                    </Box>

                    {isMobile && (
                        <Accordion
                            expanded={expandedAccordions.detail}
                            onChange={handleAccordion('detail')}
                            sx={{
                                boxShadow: 'none',
                                mb: 1,
                                border: 'none',
                                '&.MuiAccordion-root': {
                                    border: 'none',
                                    '&:before': {
                                        display: 'none',
                                    },
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                            >
                                상세정보
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '80px 1fr', md: '120px 1fr' },
                                        rowGap: 1,
                                        columnGap: 2,
                                    }}
                                >
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>제조국</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                        {product?.countryOfOrigin || '-'}
                                    </Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>색상</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>{colorInfo || '-'}</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>사이즈</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>{sizeInfo || '-'}</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>혼용률</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                        {product?.materialInfo && product.materialInfo.length > 0
                                            ? product.materialInfo
                                                  .map((m) => `${materialValueToLabel(m.material)} ${m.percentage}%`)
                                                  .join(', ')
                                            : '판매자문의'}
                                    </Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>낱장여부</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>판매자문의</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>상품등록정보</Typography>
                                    <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                        {product?.createdAt?.split('T')[0]} 등록
                                    </Typography>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {isMobile && shouldShowCareInfo() && (
                        <>
                            <Divider sx={{ borderColor: '#eee' }} />
                            <Accordion
                                expanded={expandedAccordions.care}
                                onChange={handleAccordion('care')}
                                sx={{
                                    boxShadow: 'none',
                                    mb: 1,
                                    border: 'none',
                                    '&.MuiAccordion-root': {
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                >
                                    세탁 및 상품 주의사항
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ mb: 1, fontSize: { xs: 13, md: 15 } }}>
                                        {product?.fabricInfo &&
                                            Object.values(product.fabricInfo).some((value) => value) && (
                                                <>
                                                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                        옷감 정보
                                                    </Typography>

                                                    {/* 두꺼운 구분선 추가 */}
                                                    <Divider
                                                        sx={{
                                                            my: 3,
                                                            borderBottomWidth: 1,
                                                            borderColor: '#222',
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            width: '100%',
                                                            mb: 3,
                                                            '& > div': {
                                                                display: 'flex',
                                                                borderBottom: '1px solid #eee',
                                                                '&:last-child': {
                                                                    borderBottom: 'none',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        {/* 옷감 정보 섹션 */}
                                                        {Object.entries(FABRIC_OPTIONS).map(
                                                            ([key, { label, options }]) => (
                                                                <FabricOptionRow
                                                                    key={key}
                                                                    label={label}
                                                                    options={options}
                                                                    selectedValue={
                                                                        product?.fabricInfo?.[
                                                                            key as keyof typeof FABRIC_OPTIONS
                                                                        ]
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </Box>
                                                </>
                                            )}
                                        {product?.washInfo &&
                                            Object.values(product.washInfo).some(
                                                (values) => values && values.length > 0,
                                            ) && (
                                                <>
                                                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                        세탁 정보
                                                    </Typography>
                                                    {renderWashInfo()}
                                                </>
                                            )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}

                    {/* 상품 사이즈 아코디언 */}
                    {currentSizeConfig && (
                        <>
                            <Divider sx={{ borderColor: '#eee' }} />
                            <Accordion
                                expanded={expandedAccordions.size}
                                onChange={handleAccordion('size')}
                                sx={{
                                    boxShadow: 'none',
                                    mb: 1,
                                    border: 'none',
                                    '&.MuiAccordion-root': {
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                >
                                    상품 사이즈
                                </AccordionSummary>
                                <AccordionDetails>
                                    {/* 사이즈 가이드 이미지 */}
                                    <Box
                                        sx={{
                                            mb: 3,
                                            width: '100%',
                                            textAlign: 'center',
                                            bgcolor: '#f8f8f8',
                                            borderRadius: 2,
                                            p: 3,
                                            position: 'relative',
                                            '& img': {
                                                position: 'relative',
                                                zIndex: 1,
                                                mixBlendMode: 'multiply',
                                                filter: 'contrast(1.1)',
                                            },
                                        }}
                                    >
                                        <img
                                            src={currentSizeConfig.image}
                                            alt={`${currentSizeConfig.label} 사이즈 가이드`}
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                borderRadius: 8,
                                            }}
                                        />
                                    </Box>

                                    {/* 사이즈 테이블 */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            overflowX: 'auto',
                                            '&::-webkit-scrollbar': {
                                                height: 6,
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f1f1f1',
                                                borderRadius: 3,
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#888',
                                                borderRadius: 3,
                                                '&:hover': {
                                                    backgroundColor: '#555',
                                                },
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: isMobile ? 'fit-content' : 'auto',
                                                maxWidth: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            {/* 테이블 헤더 */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    borderBottom: '1px solid #eee',
                                                    py: 1.5,
                                                }}
                                            >
                                                {/* 사이즈 열의 너비 계산 */}
                                                {(() => {
                                                    const columnCount = product?.sizeInfo?.[0]
                                                        ? Object.keys(product.sizeInfo[0]).length - 1
                                                        : 0;
                                                    const sizeColumnWidth =
                                                        columnCount <= 4
                                                            ? isMobile
                                                                ? '12%'
                                                                : '20%'
                                                            : isMobile
                                                            ? '12%'
                                                            : '15%';

                                                    return (
                                                        <Typography
                                                            sx={{
                                                                width: sizeColumnWidth,
                                                                minWidth: '60px',
                                                                fontSize: 14,
                                                                fontWeight: 700,
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            사이즈
                                                        </Typography>
                                                    );
                                                })()}

                                                {product?.sizeInfo?.[0] &&
                                                    Object.keys(product.sizeInfo[0])
                                                        .filter((key) => key !== 'size')
                                                        .map((field) => {
                                                            const columnCount =
                                                                Object.keys(product.sizeInfo[0]).length - 1;
                                                            // 열 개수에 따라 동적으로 너비 조절
                                                            const getColumnStyle = () => {
                                                                if (columnCount <= 3) {
                                                                    return {
                                                                        minWidth: '120px',
                                                                        flex: '1 1 25%',
                                                                    };
                                                                } else if (columnCount <= 5) {
                                                                    return {
                                                                        minWidth: '100px',
                                                                        flex: '1 1 20%',
                                                                    };
                                                                } else {
                                                                    return {
                                                                        minWidth: '80px',
                                                                        flex: '1 1 15%',
                                                                    };
                                                                }
                                                            };

                                                            return (
                                                                <Typography
                                                                    key={field}
                                                                    sx={{
                                                                        ...getColumnStyle(),
                                                                        fontSize: 14,
                                                                        fontWeight: 700,
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    {field}
                                                                </Typography>
                                                            );
                                                        })}
                                            </Box>

                                            {/* 동적으로 사이즈 행 생성 */}
                                            {product?.sizeInfo?.map((sizeData, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        borderBottom: '1px solid #eee',
                                                        py: 1.5,
                                                        '&:last-child': {
                                                            borderBottom: 'none',
                                                        },
                                                    }}
                                                >
                                                    {/* 사이즈 열의 너비 계산 */}
                                                    {(() => {
                                                        const columnCount = Object.keys(sizeData).length - 1;
                                                        const sizeColumnWidth =
                                                            columnCount <= 4
                                                                ? isMobile
                                                                    ? '12%'
                                                                    : '20%'
                                                                : isMobile
                                                                ? '12%'
                                                                : '15%';

                                                        return (
                                                            <Typography
                                                                sx={{
                                                                    width: sizeColumnWidth,
                                                                    minWidth: '60px',
                                                                    fontSize: 14,
                                                                    fontWeight: 700,
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                {sizeData.size}
                                                            </Typography>
                                                        );
                                                    })()}

                                                    {Object.entries(sizeData)
                                                        .filter(([key]) => key !== 'size')
                                                        .map(([key, value]) => {
                                                            const columnCount = Object.keys(sizeData).length - 1;
                                                            // 열 개수에 따라 동적으로 너비 조절
                                                            const getColumnStyle = () => {
                                                                if (columnCount <= 3) {
                                                                    return {
                                                                        minWidth: '120px',
                                                                        flex: '1 1 25%',
                                                                    };
                                                                } else if (columnCount <= 5) {
                                                                    return {
                                                                        minWidth: '100px',
                                                                        flex: '1 1 20%',
                                                                    };
                                                                } else {
                                                                    return {
                                                                        minWidth: '80px',
                                                                        flex: '1 1 15%',
                                                                    };
                                                                }
                                                            };

                                                            return (
                                                                <Typography
                                                                    key={key}
                                                                    sx={{
                                                                        ...getColumnStyle(),
                                                                        fontSize: 14,
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    {value}
                                                                </Typography>
                                                            );
                                                        })}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Typography sx={{ fontSize: 13, color: '#666', mt: 2, textAlign: 'right' }}>
                                        * 측정 방법에 따라 약간의 오차가 발생할 수 있습니다.
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}

                    {/* 제품 설명 아코디언 */}
                    <Accordion
                        expanded={expandedAccordions.desc}
                        onChange={handleAccordion('desc')}
                        sx={{
                            paddingBottom: `${isMobile ? '40px' : '0'}`,
                            boxShadow: 'none',
                            // 요구사항: 제품 설명 섹션의 상단 가로 줄 제거
                            '& .MuiCollapse-root': {
                                transition: 'height 150ms cubic-bezier(0.4, 0, 0.2, 1) !important',
                            },
                            ...(isMobile && {
                                '&:not(.Mui-expanded)': {
                                    paddingBottom: '40px',
                                },
                            }),
                            '& .MuiAccordionSummary-content': {
                                transition: 'margin 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                            },
                            '& .MuiAccordionSummary-expandIconWrapper': {
                                transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                            },
                        }}
                        TransitionProps={{
                            timeout: 150,
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: 17, md: 16 },
                                py: 2,
                            }}
                        >
                            제품 설명
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                pb: 3,
                                '& .product-description img': {
                                    // 데스크톱: 최대 가로폭 860px 제한, 모바일: 제한 없음
                                    maxWidth: { xs: '100%', md: '860px' },
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    display: 'block',
                                    margin: '0 auto',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease-in-out',
                                    // 요구사항: 둥근 모서리 및 그림자 제거
                                    borderRadius: 0,
                                    boxShadow: 'none',
                                },
                                '& .product-description img[src]': {
                                    opacity: 1,
                                },
                            }}
                        >
                            {shouldRenderDetailThumbnails && isThumbPositionTop && renderDetailThumbnails()}
                            <Box
                                className="product-description"
                                dangerouslySetInnerHTML={{
                                    __html: (product?.description || '').replace(
                                        /<figure class="image">([\s\S]*?)<\/figure>/g,
                                        '$1',
                                    ),
                                }}
                                sx={{
                                    overflow: 'hidden',
                                    wordWrap: 'break-word',
                                    '& p': {
                                        margin: '0 0 16px 0',
                                        padding: 0,
                                        lineHeight: 1.6,
                                    },
                                    '& center': {
                                        display: 'block',
                                        textAlign: 'center',
                                        margin: '16px 0',
                                    },
                                }}
                            />
                            {shouldRenderDetailThumbnails && !isThumbPositionTop && renderDetailThumbnails()}
                        </AccordionDetails>
                    </Accordion>
                </Box>
                {/* 모바일 하단 장바구니 담기 버튼 */}
                {isMobile && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            bgcolor: '#fff',
                            borderTop: '1px solid #eee',
                            zIndex: 1000,
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        {isLogined() && totalPrice > 0 && (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography sx={{ fontSize: 12, color: '#666' }}>총 금액</Typography>
                                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                                    ₩{totalPrice.toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<ShoppingCartIcon />}
                            onClick={handleAddToCart}
                            sx={{
                                height: 56,
                                bgcolor: isLogined() ? '#000' : '#666',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 700,
                                '&:hover': {
                                    bgcolor: isLogined() ? '#222' : '#555',
                                },
                            }}
                        >
                            {isLogined() ? '장바구니 담기' : '로그인 후 주문하기'}
                        </Button>
                    </Box>
                )}

                {/* 스크롤 탑 버튼 */}
                {showScrollTop && (
                    <Box
                        onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            document.getElementById('mainContentRef')?.scrollTo?.({ top: 0, behavior: 'smooth' });
                        }}
                        sx={{
                            position: 'sticky',
                            p: { xs: 2.5, md: 3 },
                            bottom: { xs: 35, md: 24 },
                            right: { xs: 16, md: 24 },
                            marginLeft: 'auto',
                            width: { xs: 40, md: 48 },
                            height: { xs: 40, md: 48 },
                            background: 'rgba(255,255,255,0.95)',
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                background: 'rgba(255,255,255,1)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                            },
                            zIndex: 999,
                        }}
                    >
                        <ArrowUpwardIcon
                            sx={{
                                fontSize: { xs: 24, md: 28 },
                                color: '#222',
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default ProductDetailContent;
